<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3"
import { computed } from "vue"
import {
  ArchiveBoxIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  CalculatorIcon,
  CubeIcon,
  CurrencyDollarIcon,
  EnvelopeIcon,
  HomeIcon,
  PlusCircleIcon,
  ReceiptPercentIcon,
  ScaleIcon,
  Squares2X2Icon,
  SwatchIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ShoppingCartIcon,
} from "@heroicons/vue/24/outline"
import { navigationSections, type RoleKey, type NavigationItem } from "../navigation"

type PageProps = {
  url: string
  auth?: {
    role_keys?: RoleKey[]
  }
}

const page = usePage<PageProps>()
const currentPath = computed(() => page.url.split("?")[0])
const roleKeys = computed(() => page.props.auth?.role_keys ?? [])

const visibleSections = computed(() => {
  return navigationSections
    .map((section) => {
      const items = section.items.filter((item) => {
        if (!item.allowedRoles || item.allowedRoles.length === 0) return true
        return item.allowedRoles.some((role) => roleKeys.value.includes(role))
      })

      return { ...section, items }
    })
    .filter((section) => section.items.length > 0)
})

function isActive(path: string): boolean {
  if (path === "/dashboard") {
    return currentPath.value === "/" || currentPath.value === "/dashboard"
  }

  return currentPath.value.startsWith(path)
}

const iconsByKey = {
  home: HomeIcon,
  cube: CubeIcon,
  squares: Squares2X2Icon,
  archive: ArchiveBoxIcon,
  shoppingCart: ShoppingCartIcon,
  currencyDollar: CurrencyDollarIcon,
  wrench: WrenchScrewdriverIcon,
  users: UserGroupIcon,
  buildingStorefront: BuildingStorefrontIcon,
  scale: ScaleIcon,
  receiptPercent: ReceiptPercentIcon,
  calculator: CalculatorIcon,
  envelope: EnvelopeIcon,
  buildingOffice: BuildingOfficeIcon,
  plusCircle: PlusCircleIcon,
  swatch: SwatchIcon,
} as const

function iconFor(item: NavigationItem) {
  return iconsByKey[item.icon]
}
</script>

<template>
  <aside class="h-full w-64 shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-4 py-6">
    <nav class="space-y-6">
      <section v-for="section in visibleSections" :key="section.title" class="space-y-1">
        <p class="px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{{ section.title }}</p>

        <Link
          v-for="item in section.items"
          :key="item.href"
          :href="item.href"
          class="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition"
          :class="isActive(item.href) ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'"
        >
          <component :is="iconFor(item)" class="size-4 shrink-0" />
          <span>{{ item.label }}</span>
        </Link>
      </section>
    </nav>
  </aside>
</template>

